/**
 * ApplicationContext.tsx
 * 
 * Context for managing application state across all steps
 * Provides smart state persistence and navigation capabilities
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef, ReactNode } from 'react';
import { DocumentUploadResponse } from '../types/application.types';

// Application step types
export type ApplicationStep = 'info' | 'upload' | 'choice' | 'processing' | 'form' | 'review' | 'complete';

// Application state interface
export interface ApplicationState {
  // Current step and navigation
  currentStep: ApplicationStep;
  stepHistory: ApplicationStep[];
  
  // Applicant information
  applicantInfo: {
    fullName: string;
    emailAddress: string;
    companyEmail: string;
    personalEmail: string;
    secondaryEmail: string;
    preferredContactChannel: 'company' | 'personal' | null;
    companyName: string;
    companyWebsite: string;
    notificationConsent: boolean;
    marketingConsent: boolean;
  };
  
  // Document upload state
  uploadedFiles: File[];
  uploadedDocuments: DocumentUploadResponse[];
  
  // AI processing state
  isProcessing: boolean;
  processingStage: string;
  processingProgress: number;
  elapsedTime: number;
  processingLogs: string[];
  tokensUsed: number;
  aiConfidence: number;
  aiProcessingComplete: boolean;
  
  // Application data
  applicationId: string | null;
  applicationData: any;
  extractedData: any;
  formData: any;
  
  // Navigation and UI state
  hasUnsavedChanges: boolean;
  processingStartTime: Date | null;
  lastSaved: Date | null;
}

// Action types
export type ApplicationAction =
  | { type: 'SET_CURRENT_STEP'; payload: ApplicationStep }
  | { type: 'SET_APPLICANT_INFO'; payload: Partial<ApplicationState['applicantInfo']> }
  | { type: 'SET_UPLOADED_FILES'; payload: File[] }
  | { type: 'ADD_UPLOADED_FILES'; payload: File[] }
  | { type: 'SET_UPLOADED_DOCUMENTS'; payload: DocumentUploadResponse[] }
  | { type: 'ADD_UPLOADED_DOCUMENTS'; payload: DocumentUploadResponse[] }
  | { type: 'REMOVE_UPLOADED_FILE'; payload: { fileName: string } }
  | { type: 'SET_PROCESSING_STATE'; payload: Partial<Pick<ApplicationState, 'isProcessing' | 'processingStage' | 'processingProgress' | 'elapsedTime' | 'processingLogs' | 'tokensUsed' | 'aiConfidence' | 'processingStartTime'>> }
  | { type: 'SET_AI_PROCESSING_COMPLETE'; payload: boolean }
  | { type: 'SET_APPLICATION_ID'; payload: string }
  | { type: 'SET_APPLICATION_DATA'; payload: any }
  | { type: 'SET_EXTRACTED_DATA'; payload: any }
  | { type: 'SET_FORM_DATA'; payload: any }
  | { type: 'SET_HAS_UNSAVED_CHANGES'; payload: boolean }
  | { type: 'SET_PROCESSING_START_TIME'; payload: Date }
  | { type: 'SET_LAST_SAVED'; payload: Date }
  | { type: 'RESET_STATE' }
  | { type: 'RESTORE_STATE'; payload: Partial<ApplicationState> };

// Initial state
const initialState: ApplicationState = {
  currentStep: 'info',
  stepHistory: ['info'],
  applicantInfo: {
    fullName: '',
    emailAddress: '',
    companyEmail: '',
    personalEmail: '',
    secondaryEmail: '',
    preferredContactChannel: null,
    companyName: '',
    companyWebsite: '',
    notificationConsent: false,
    marketingConsent: false
  },
  uploadedFiles: [],
  uploadedDocuments: [],
  isProcessing: false,
  processingStage: 'saving',
  processingProgress: 0,
  elapsedTime: 0,
  processingLogs: [],
  tokensUsed: 0,
  aiConfidence: 0,
  aiProcessingComplete: false,
  applicationId: null,
  applicationData: null,
  extractedData: null,
  formData: null,
  hasUnsavedChanges: false,
  processingStartTime: null,
  lastSaved: null
};

function readStoredSessionId(): string | null {
  try {
    const value = localStorage.getItem('voluntas_session_id');
    return value && value.startsWith('session_') ? value : null;
  } catch {
    return null;
  }
}

/** Resume-critical fields only — omit heavy Firestore mirrors from localStorage */
function buildPersistedApplicationState(state: ApplicationState) {
  return {
    currentStep: state.currentStep,
    stepHistory: state.stepHistory,
    applicationId: state.applicationId,
    applicantInfo: state.applicantInfo,
    uploadedDocuments: state.uploadedDocuments,
    aiProcessingComplete: state.aiProcessingComplete,
    hasUnsavedChanges: state.hasUnsavedChanges,
    processingStartTime: state.processingStartTime?.toISOString() ?? null,
    lastSaved: state.lastSaved?.toISOString() ?? null,
    sessionId: readStoredSessionId(),
    isProcessing: false,
    processingStage: 'saving',
    processingProgress: 0,
    elapsedTime: 0,
    processingLogs: [],
    tokensUsed: 0,
    aiConfidence: 0
  };
}

// Reducer function
function applicationReducer(state: ApplicationState, action: ApplicationAction): ApplicationState {
  switch (action.type) {
    case 'SET_CURRENT_STEP':
      return {
        ...state,
        currentStep: action.payload,
        stepHistory: [...state.stepHistory, action.payload]
      };
    
    case 'SET_APPLICANT_INFO':
      return {
        ...state,
        applicantInfo: { ...state.applicantInfo, ...action.payload },
        hasUnsavedChanges: true
      };
    
    case 'SET_UPLOADED_FILES':
      return {
        ...state,
        uploadedFiles: action.payload,
        hasUnsavedChanges: true
      };
      
    case 'ADD_UPLOADED_FILES':
      return {
        ...state,
        uploadedFiles: [...state.uploadedFiles, ...action.payload],
        hasUnsavedChanges: true
      };
    
    case 'SET_UPLOADED_DOCUMENTS':
      return {
        ...state,
        uploadedDocuments: action.payload,
        hasUnsavedChanges: true
      };

    case 'ADD_UPLOADED_DOCUMENTS':
      return {
        ...state,
        uploadedDocuments: [...state.uploadedDocuments, ...action.payload],
        hasUnsavedChanges: true
      };
    
    case 'REMOVE_UPLOADED_FILE':
      return {
        ...state,
        uploadedFiles: state.uploadedFiles.filter(file => file.name !== action.payload.fileName),
        uploadedDocuments: state.uploadedDocuments.filter(doc => doc.fileName !== action.payload.fileName),
        hasUnsavedChanges: true
      };
    
    case 'SET_PROCESSING_STATE':
      return {
        ...state,
        ...action.payload
      };
    
    case 'SET_AI_PROCESSING_COMPLETE':
      return {
        ...state,
        aiProcessingComplete: action.payload
      };
    
    case 'SET_APPLICATION_ID':
      return {
        ...state,
        applicationId: action.payload
      };
    
    case 'SET_APPLICATION_DATA':
      return {
        ...state,
        applicationData: action.payload
      };
    
    case 'SET_EXTRACTED_DATA':
      return {
        ...state,
        extractedData: action.payload
      };
    
    case 'SET_FORM_DATA':
      return {
        ...state,
        formData: action.payload,
        hasUnsavedChanges: true
      };
    
    case 'SET_HAS_UNSAVED_CHANGES':
      return {
        ...state,
        hasUnsavedChanges: action.payload
      };
    
    case 'SET_PROCESSING_START_TIME':
      return {
        ...state,
        processingStartTime: action.payload
      };
    
    case 'SET_LAST_SAVED':
      return {
        ...state,
        lastSaved: action.payload,
        hasUnsavedChanges: false
      };
    
    case 'RESET_STATE':
      return initialState;
    
    case 'RESTORE_STATE':
      return {
        ...state,
        ...action.payload
      };
    
    default:
      return state;
  }
}

// Context interface
interface ApplicationContextType {
  state: ApplicationState;
  dispatch: React.Dispatch<ApplicationAction>;
  
  // Navigation methods
  goToStep: (step: ApplicationStep) => void;
  goBack: () => void;
  goForward: () => void;
  canGoBack: () => boolean;
  canGoForward: () => boolean;
  
  // State management methods
  updateApplicantInfo: (info: Partial<ApplicationState['applicantInfo']>) => void;
  addUploadedFiles: (files: File[]) => void;
  addUploadedDocuments: (documents: DocumentUploadResponse[]) => void;
  removeUploadedFile: (fileName: string) => void;
  setProcessingState: (processingState: Partial<Pick<ApplicationState, 'isProcessing' | 'processingStage' | 'processingProgress' | 'elapsedTime' | 'processingLogs' | 'tokensUsed' | 'aiConfidence' | 'processingStartTime'>>) => void;
  setAIProcessingComplete: (complete: boolean) => void;
  setApplicationId: (id: string) => void;
  setApplicationData: (data: any) => void;
  setExtractedData: (data: any) => void;
  setFormData: (data: any) => void;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  setLastSaved: (date: Date) => void;
  
  // Utility methods
  resetState: () => void;
  saveToLocalStorage: () => void;
  loadFromLocalStorage: () => void;
  clearLocalStorage: () => void;
}

// Create context
const ApplicationContext = createContext<ApplicationContextType | undefined>(undefined);

// Hook to use the context
export const useApplication = () => {
  const context = useContext(ApplicationContext);
  if (context === undefined) {
    throw new Error('useApplication must be used within an ApplicationProvider');
  }
  return context;
};

// Provider component
interface ApplicationProviderProps {
  children: ReactNode;
}

export const ApplicationProvider: React.FC<ApplicationProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(applicationReducer, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;
  const saveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const writePersistedState = useCallback((snapshot: ApplicationState) => {
    try {
      localStorage.setItem(
        'voluntas_application_state',
        JSON.stringify(buildPersistedApplicationState(snapshot))
      );
    } catch {
      // Intentionally silent in production; could report to monitoring
    }
  }, []);

  const saveToLocalStorage = useCallback(() => {
    writePersistedState(stateRef.current);
  }, [writePersistedState]);

  const scheduleSaveToLocalStorage = useCallback(() => {
    if (saveDebounceRef.current) {
      clearTimeout(saveDebounceRef.current);
    }
    saveDebounceRef.current = setTimeout(() => {
      saveDebounceRef.current = null;
      writePersistedState(stateRef.current);
    }, 400);
  }, [writePersistedState]);

  const loadFromLocalStorage = useCallback(() => {
    try {
      const savedState = localStorage.getItem('voluntas_application_state');
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        if (parsedState.applicantInfo) {
          const applicantInfo = parsedState.applicantInfo;
          const legacyEmail = typeof applicantInfo.emailAddress === 'string' ? applicantInfo.emailAddress : '';
          const hasCompanyEmail = typeof applicantInfo.companyEmail === 'string' && applicantInfo.companyEmail.trim().length > 0;
          const hasPersonalEmail = typeof applicantInfo.personalEmail === 'string' && applicantInfo.personalEmail.trim().length > 0;

          parsedState.applicantInfo = {
            ...initialState.applicantInfo,
            ...applicantInfo,
            companyEmail: hasCompanyEmail ? applicantInfo.companyEmail : legacyEmail,
            personalEmail: hasPersonalEmail ? applicantInfo.personalEmail : '',
            secondaryEmail: typeof applicantInfo.secondaryEmail === 'string' ? applicantInfo.secondaryEmail : '',
            preferredContactChannel: applicantInfo.preferredContactChannel === 'company' || applicantInfo.preferredContactChannel === 'personal'
              ? applicantInfo.preferredContactChannel
              : hasCompanyEmail || legacyEmail
                ? 'company'
                : hasPersonalEmail
                  ? 'personal'
                  : null,
            notificationConsent: Boolean(applicantInfo.notificationConsent),
            marketingConsent: Boolean(applicantInfo.marketingConsent)
          };
        }
        if (parsedState.processingStartTime) {
          parsedState.processingStartTime = new Date(parsedState.processingStartTime);
        }
        if (parsedState.lastSaved) {
          parsedState.lastSaved = new Date(parsedState.lastSaved);
        }
        const savedSession = typeof parsedState.sessionId === 'string' ? parsedState.sessionId : '';
        if (savedSession.startsWith('session_')) {
          try {
            const current = localStorage.getItem('voluntas_session_id');
            if (!current || current.startsWith('sess_')) {
              localStorage.setItem('voluntas_session_id', savedSession);
            }
          } catch {
            // ignore
          }
        }
        delete parsedState.sessionId;
        dispatch({ type: 'RESTORE_STATE', payload: parsedState });
      }
    } catch (error) {
      // Intentionally silent in production; could report to monitoring
    }
  }, []);

  const clearLocalStorage = useCallback(() => {
    try {
      localStorage.removeItem('voluntas_application_state');
    } catch (error) {
      // Intentionally silent in production; could report to monitoring
    }
  }, []);

  // Load state from localStorage on mount
  useEffect(() => {
    loadFromLocalStorage();
  }, [loadFromLocalStorage]);

  // Save state to localStorage when it changes (debounced, slim payload)
  useEffect(() => {
    if (state.currentStep !== 'info' || state.hasUnsavedChanges) {
      scheduleSaveToLocalStorage();
    }
    return () => {
      if (saveDebounceRef.current) {
        clearTimeout(saveDebounceRef.current);
        saveDebounceRef.current = null;
      }
    };
  }, [state, scheduleSaveToLocalStorage]);

  // Flush pending resume state before tab close
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (saveDebounceRef.current) {
        clearTimeout(saveDebounceRef.current);
        saveDebounceRef.current = null;
      }
      writePersistedState(stateRef.current);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [writePersistedState]);

  // Navigation methods
  const goToStep = useCallback((step: ApplicationStep) => {
    dispatch({ type: 'SET_CURRENT_STEP', payload: step });
    
    // Auto-scroll to top on every step transition with smooth behavior
    setTimeout(() => {
      window.scrollTo({ 
        top: 0, 
        behavior: 'smooth' 
      });
    }, 100); // Small delay to ensure DOM update completes
  }, []);

  const goBack = useCallback(() => {
    const currentIndex = state.stepHistory.indexOf(state.currentStep);
    if (currentIndex > 0) {
      const previousStep = state.stepHistory[currentIndex - 1];
      dispatch({ type: 'SET_CURRENT_STEP', payload: previousStep });
      
      // Auto-scroll to top on step transition
      setTimeout(() => {
        window.scrollTo({ 
          top: 0, 
          behavior: 'smooth' 
        });
      }, 100);
    }
  }, [state.stepHistory, state.currentStep]);

  const goForward = useCallback(() => {
    const currentIndex = state.stepHistory.indexOf(state.currentStep);
    if (currentIndex < state.stepHistory.length - 1) {
      const nextStep = state.stepHistory[currentIndex + 1];
      dispatch({ type: 'SET_CURRENT_STEP', payload: nextStep });
      
      // Auto-scroll to top on step transition
      setTimeout(() => {
        window.scrollTo({ 
          top: 0, 
          behavior: 'smooth' 
        });
      }, 100);
    }
  }, [state.stepHistory, state.currentStep]);

  const canGoBack = useCallback(() => {
    const currentIndex = state.stepHistory.indexOf(state.currentStep);
    return currentIndex > 0;
  }, [state.stepHistory, state.currentStep]);

  const canGoForward = useCallback(() => {
    const currentIndex = state.stepHistory.indexOf(state.currentStep);
    return currentIndex < state.stepHistory.length - 1;
  }, [state.stepHistory, state.currentStep]);

  // State management methods
  const updateApplicantInfo = useCallback((info: Partial<ApplicationState['applicantInfo']>) => {
    dispatch({ type: 'SET_APPLICANT_INFO', payload: info });
  }, []);

  const addUploadedFiles = useCallback((files: File[]) => {
    dispatch({ type: 'ADD_UPLOADED_FILES', payload: files });
  }, []);

  const addUploadedDocuments = useCallback((documents: DocumentUploadResponse[]) => {
    dispatch({ type: 'ADD_UPLOADED_DOCUMENTS', payload: documents });
  }, []);

  const removeUploadedFile = useCallback((fileName: string) => {
    dispatch({ type: 'REMOVE_UPLOADED_FILE', payload: { fileName } });
  }, []);

  const setProcessingState = useCallback((processingState: Partial<Pick<ApplicationState, 'isProcessing' | 'processingStage' | 'processingProgress' | 'elapsedTime' | 'processingLogs' | 'tokensUsed' | 'aiConfidence' | 'processingStartTime'>>) => {
    dispatch({ type: 'SET_PROCESSING_STATE', payload: processingState });
  }, []);

  const setAIProcessingComplete = useCallback((complete: boolean) => {
    dispatch({ type: 'SET_AI_PROCESSING_COMPLETE', payload: complete });
  }, []);

  const setApplicationId = useCallback((id: string) => {
    dispatch({ type: 'SET_APPLICATION_ID', payload: id });
  }, []);

  const setApplicationData = useCallback((data: any) => {
    dispatch({ type: 'SET_APPLICATION_DATA', payload: data });
  }, []);

  const setExtractedData = useCallback((data: any) => {
    dispatch({ type: 'SET_EXTRACTED_DATA', payload: data });
  }, []);

  const setFormData = useCallback((data: any) => {
    dispatch({ type: 'SET_FORM_DATA', payload: data });
  }, []);

  const setHasUnsavedChanges = useCallback((hasChanges: boolean) => {
    dispatch({ type: 'SET_HAS_UNSAVED_CHANGES', payload: hasChanges });
  }, []);

  const setLastSaved = useCallback((date: Date) => {
    dispatch({ type: 'SET_LAST_SAVED', payload: date });
  }, []);

  // Utility methods
  const resetState = useCallback(() => {
    dispatch({ type: 'RESET_STATE' });
    clearLocalStorage();
  }, [clearLocalStorage]);

  const value: ApplicationContextType = {
    state,
    dispatch,
    goToStep,
    goBack,
    goForward,
    canGoBack,
    canGoForward,
    updateApplicantInfo,
    addUploadedFiles,
    addUploadedDocuments,
    removeUploadedFile,
    setProcessingState,
    setAIProcessingComplete,
    setApplicationId,
    setApplicationData,
    setExtractedData,
    setFormData,
    setHasUnsavedChanges,
    setLastSaved,
    resetState,
    saveToLocalStorage,
    loadFromLocalStorage,
    clearLocalStorage
  };

  return (
    <ApplicationContext.Provider value={value}>
      {children}
    </ApplicationContext.Provider>
  );
}; 